import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ErreurApi, api, surSessionPerdue } from '../lib/apiClient';
import type { EntiteDeSession, Session } from '../types';

interface ContexteAuth {
  session: Session | null;
  chargement: boolean;
  /** La session a expire PENDANT l'usage : la page de connexion le dit. */
  sessionExpiree: boolean;
  entiteActive: EntiteDeSession | null;
  connecter: (identifiant: string, motDePasse: string) => Promise<void>;
  deconnecter: () => Promise<void>;
  changerEntite: (entiteId: string) => Promise<void>;
  rafraichir: () => Promise<void>;
  /**
   * RG-35 : l'interface interroge les permissions nommees, jamais le nom
   * du role. Elle masque ce que l'utilisateur ne peut pas faire ; le
   * backend le refuse de toute facon — les deux controles sont
   * independants, et c'est celui du serveur qui fait foi.
   */
  peut: (...permissions: string[]) => boolean;
}

const Contexte = createContext<ContexteAuth | null>(null);

export function FournisseurAuth({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [chargement, setChargement] = useState(true);
  // Vrai quand la session a ete PERDUE en cours de route, pour distinguer
  // « reconnectez-vous » d'une premiere visite.
  const [sessionExpiree, setSessionExpiree] = useState(false);

  const rafraichir = useCallback(async () => {
    try {
      const courante = await api.get<Session>('/api/auth/session');
      setSession(courante);
    } catch (erreur) {
      if (erreur instanceof ErreurApi && erreur.estNonAuthentifie) {
        setSession(null);
      } else {
        throw erreur;
      }
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void rafraichir();
  }, [rafraichir]);

  /*
   * La session peut tomber PENDANT l'usage — delai d'inactivite, ou
   * revocation depuis l'ecran Utilisateurs. Sans cet abonnement, elle
   * restait en memoire : le routage laissait passer et chaque ecran
   * affichait « Session expiree » dans un encart, sans jamais renvoyer a
   * la connexion.
   */
  useEffect(
    () =>
      surSessionPerdue(() => {
        setSession(null);
        setSessionExpiree(true);
      }),
    [],
  );

  const connecter = useCallback(
    async (identifiant: string, motDePasse: string) => {
      await api.post('/api/auth/connexion', { identifiant, motDePasse });
      setSessionExpiree(false);
      await rafraichir();
    },
    [rafraichir],
  );

  const deconnecter = useCallback(async () => {
    try {
      await api.post('/api/auth/deconnexion');
    } finally {
      setSession(null);
    }
  }, []);

  const changerEntite = useCallback(
    async (entiteId: string) => {
      await api.post('/api/auth/entite-active', { entiteId });
      // Les permissions dependent de l'entite : la session doit etre
      // rechargee, pas seulement l'identifiant d'entite mis a jour.
      await rafraichir();
    },
    [rafraichir],
  );

  const entiteActive = useMemo(
    () => session?.entites.find((entite) => entite.id === session.entiteActiveId) ?? null,
    [session],
  );

  const peut = useCallback(
    (...permissions: string[]) =>
      permissions.some((permission) => session?.permissions.includes(permission) ?? false),
    [session],
  );

  const valeur = useMemo(
    () => ({
      session,
      chargement,
      sessionExpiree,
      entiteActive,
      connecter,
      deconnecter,
      changerEntite,
      rafraichir,
      peut,
    }),
    [
      session,
      chargement,
      sessionExpiree,
      entiteActive,
      connecter,
      deconnecter,
      changerEntite,
      rafraichir,
      peut,
    ],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): ContexteAuth {
  const contexte = useContext(Contexte);
  if (!contexte) {
    throw new Error('useAuth doit etre utilise dans un FournisseurAuth');
  }
  return contexte;
}
